import { ReactNode, SetStateAction, createContext, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { updateUserSelectedEdge } from 'redux/slices/edgeSlice';
import Vec2 from 'vec2';

import { autoPlaceTranslation } from '@canvas-common';
import { CANVAS_OBJECT_TYPE_PREFIX, FAUX_SHAPE_INDEX } from '@common/constants';
import { ShapeType } from '@common/enums';
import { DrawingTabEnum } from '@common/enums/drawing-tab.enum';
import { CustomSideActionType, DrawingAppliance, DrawingShape, EdgeModel } from '@common/models';
import useUpdateWithHistory from '@core/hooks/useUpdateWithHistory';
import useColors from '@ee-api/colors-api/useColors';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import {
    clearDrawingShapesUndoStack as clearDrawingShapesUndoStackAction,
    decDrawingShapesUndoStackIndex,
    incDrawingShapesUndoStackIndex,
    pushDrawingShapesUndoStack as pushDrawingShapesUndoStackAction,
    resetActiveDrawingAppliance,
    resetClearedShapeEditNotifications,
    selectActiveDrawingAppliance,
    selectDrawingColorUndoStack,
    selectDrawingEdgeUndoStack,
    selectDrawingShapesUndoStackIndex,
    selectHighestLevelUndoStackIndex,
    setActiveDrawingAppliance,
} from '@redux/slices/drawingShapeSlice';
import { updateSelectedDrawingTab, updateSelectedDrawingTabOnApplianceSelection } from '@redux/slices/shapeSlice';

import useAppliance from '../components/scrollable-area/appliances/useAppliance';
import drawingTool from '../components/scrollable-area/design-countertop/drawer/drawingTool';

/**
 * Something drawn might be a shape or an appliance
 */
export type DrawingItem = DrawingShape | string;

/**
 * Type guard to check if drawingItem is a DrawingShape ( it might also be for instance an appliance)
 */
export const isDrawingShape = (activeDrawingItem?: DrawingItem): activeDrawingItem is DrawingShape => {
    return !!activeDrawingItem && !!(activeDrawingItem as DrawingShape).type;
};

type DrawingShapeContextType =
    | Record<string, never> // this is just to allow an empty object as the default
    | {
          areThereShapesInTheDrawingArea?: boolean;
          activeDrawingItem: DrawingItem | undefined;
          activeDrawingApplianceName?: string;
          drawingShapes: DrawingShape[];
          updateDrawingShapes: (value: SetStateAction<DrawingShape[]>) => void;
          addMultipleDrawingShapes: (shapes: DrawingShape[]) => void;
          activeDrawingShape?: DrawingShape;
          /**
           *  @deprecated use {@link updateActiveDrawingShapeId}.
           *  This function not only updates the activeDrawingShape but also resets appliance and updates selectedDrawingTab.
           *  {@link updateActiveDrawingShapeId} only updates the shape. Other functions may be necessary to be called.
           * */
          updateActiveDrawingShape: (value?: DrawingShape) => void;
          updateActiveDrawingAppliance: (value: string) => void;
          drawingShapesUndoStack?: DrawingShape[][];
          pushDrawingShapesUndoStack: (value: DrawingShape[], selectedEdge: EdgeModel) => void;
          pullDrawingShapesUndoStack: (undoing: boolean) => DrawingShape[] | undefined;
          clearDrawingShapesUndoStack: () => void;
          resetDrawingShapes: () => void;
          autoPlaceShapeTranslation: (newPoly: Vec2[]) => Vec2;
          resetActiveShape: () => void;
          updateCustomSideAction: (value: CustomSideActionType | undefined) => void;
          lastActiveDrawingShape?: DrawingShape;
          updateActiveDrawingShapeId: (activeDrawingShapeId: string | undefined) => void;
      };

export const DrawingShapeContext = createContext<DrawingShapeContextType>({});

export const DrawingShapeContextProvider = ({ children }: { children?: ReactNode }): JSX.Element => {
    const dispatch = useAppDispatch();

    const [drawingShapes, updateDrawingShapes] = useState<DrawingShape[]>([drawingTool.buildFauxShape()]);

    /**
     * This is the user selected active drawingShape
     */
    const [userSelectedActiveDrawingShape, _setActiveDrawingShape] = useState<DrawingShape>();

    const [lastActiveDrawingShape, setLastActiveDrawingShape] = useState<DrawingShape>();
    const [customSideAction, setCustomSideAction] = useState<CustomSideActionType | undefined>(undefined);
    const [drawingShapesUndoStack] = useState<DrawingShape[][]>([]);

    const drawingEdgeUndoStack = useAppSelector(selectDrawingEdgeUndoStack);
    const drawingColorUndoStack = useAppSelector(selectDrawingColorUndoStack);

    const drawingShapesUndoStackIndex = useAppSelector(selectDrawingShapesUndoStackIndex);
    const highestLevelUndoStackIndex = useAppSelector(selectHighestLevelUndoStackIndex);
    const activeDrawingAppliance = useAppSelector(selectActiveDrawingAppliance);
    const { colors } = useColors();
    const { selectedValue: selectedColor } = useSelectedColor();

    const { getApproximateApplianceOutline } = useAppliance();
    // const { getSideItems, updateSideItem } = useShapeTabContent();

    const { setSelectedColorWithHistory } = useUpdateWithHistory();

    /**
     * The difference with userSelectedDrawingShape is that it checks against drawingShapes to avoid stale data.
     * This is in place to support the now deprecated updateActiveDrawingShape. That function updated activeDrawingShape with a full record.
     * There was no guarantee that the record saved as active still was still included in the drawingShapes array that is the source of truth for drawingShapes in the design.
     */
    const _activeDrawingShape = useMemo(() => {
        return drawingShapes.find((dShape: DrawingShape) => dShape.id === userSelectedActiveDrawingShape?.id);
    }, [userSelectedActiveDrawingShape?.id, drawingShapes]);

    const addMultipleDrawingShapes = (shapes: DrawingShape[]) => {
        updateDrawingShapes((prevShapes) => [...prevShapes, ...shapes]);
    };

    /**
     * An item in the drawing that is active.
     * drawingShapes[0] is the Faux shape. So using drawingShapes[1] for fallback instead.
     */
    const activeDrawingItem: DrawingItem | undefined =
        activeDrawingAppliance ?? _activeDrawingShape ?? drawingShapes[1];

    /** The active item if is a drawing shape  otherwise undefined */
    const activeDrawingShape = isDrawingShape(activeDrawingItem) ? activeDrawingItem : undefined;

    /**Reset - TODO don't think we need this anymore! */
    useEffect(() => {
        if (customSideAction) {
            setCustomSideAction(undefined);
        }
    }, [customSideAction]);

    /**
     * This replaces updateActiveDrawingShape but does not modify the drawingTab (not its business)
     * TODO: should take a DrawingShape and find by ID, then set that shape
     * @param activeDrawingShapeId
     */
    const updateActiveDrawingShapeId = (activeDrawingShapeId?: string) => {
        /**Support for legacy full record */
        const activeDrawingShape = drawingShapes.find((dShape: DrawingShape) => dShape.id === activeDrawingShapeId);
        _setActiveDrawingShape(activeDrawingShape);
    };

    const updateActiveDrawingShape = (value?: DrawingShape) => {
        dispatch(resetActiveDrawingAppliance());
        _setActiveDrawingShape(value);
        if (value) {
            setLastActiveDrawingShape(value);
            dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
        }
    };

    const updateCustomSideAction = (value: CustomSideActionType | undefined) => {
        setCustomSideAction(value);
    };

    const updateActiveDrawingAppliance = (value: string) => {
        updateActiveDrawingShape(undefined);
        dispatch(setActiveDrawingAppliance(value));
        dispatch(updateSelectedDrawingTabOnApplianceSelection());
    };

    const resetActiveShape = () => {
        dispatch(resetActiveDrawingAppliance());
        updateActiveDrawingShape(undefined);
        dispatch(updateSelectedDrawingTabOnApplianceSelection());
    };

    const pushDrawingShapesUndoStack = (value: DrawingShape[], selectedEdge: EdgeModel) => {
        if (selectedEdge && selectedColor) {
            drawingShapesUndoStack.splice(drawingShapesUndoStackIndex, drawingShapesUndoStack.length, value);
            dispatch(
                pushDrawingShapesUndoStackAction({
                    selectedColorName: selectedColor.name,
                    selectedEdge,
                    drawingShapesUndoStackLength: drawingShapesUndoStack.length,
                })
            );
        }
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const pullDrawingShapesUndoStack = (undoing: boolean): DrawingShape[] | undefined => {
        if (undoing) {
            if (drawingShapesUndoStackIndex < 1) {
                return;
            } else {
                dispatch(decDrawingShapesUndoStackIndex());
                if (drawingShapesUndoStackIndex < 2) {
                    return;
                }
            }
        } else if (drawingShapesUndoStackIndex > highestLevelUndoStackIndex) {
            // redoing
            return;
        } else {
            dispatch(incDrawingShapesUndoStackIndex());
        }

        let pulledShapes: DrawingShape[];
        if (drawingShapesUndoStackIndex !== undefined) {
            const pullIndex = drawingShapesUndoStackIndex - (undoing ? 2 : 0);

            pulledShapes = drawingTool.cloneShapes(drawingShapesUndoStack[pullIndex]);

            const newEdge: EdgeModel | undefined = drawingEdgeUndoStack.at(pullIndex);
            dispatch(updateUserSelectedEdge(newEdge));

            const newColorName = drawingColorUndoStack.at(pullIndex);
            const newSelectedColor = colors.find((color) => color.name === newColorName);
            if (newSelectedColor) {
                setSelectedColorWithHistory(newSelectedColor);
                // dispatch(updateSelectedColor(newSelectedColor));
            }

            return newSelectedColor ? pulledShapes : undefined;
        }

        return undefined;
    };

    const autoPlaceShapeTranslation = (newPoly: Vec2[]): Vec2 => {
        let currentPolys: Vec2[][] = drawingShapes
            .filter((shape: DrawingShape) => shape.type !== ShapeType.FAUX_SHAPE)
            .map((shape: DrawingShape) => {
                return shape.highlightPoints ?? [];
            });
        // add any free standing appliances
        const freeStandingApplianceOutlines = drawingShapes[FAUX_SHAPE_INDEX].appliances?.map(
            (appliance: DrawingAppliance) => {
                return getApproximateApplianceOutline(appliance);
            }
        );
        if (freeStandingApplianceOutlines) {
            currentPolys = [...currentPolys, ...freeStandingApplianceOutlines];
        }
        return autoPlaceTranslation(newPoly, currentPolys);
    };

    const clearDrawingShapesUndoStack = (): void => {
        dispatch(clearDrawingShapesUndoStackAction());
        drawingShapesUndoStack.splice(0, drawingShapesUndoStack.length);
    };

    const resetDrawingShapes = () => {
        dispatch(resetClearedShapeEditNotifications());
        updateDrawingShapes([drawingTool.buildFauxShape()]);
    };

    const areThereShapesInTheDrawingArea = useMemo(() => {
        return drawingShapes.some(
            (shape: DrawingShape) =>
                shape.id !== ShapeType.FAUX_SHAPE || (shape.id === ShapeType.FAUX_SHAPE && shape.appliances.length > 0)
        );
    }, [drawingShapes]);

    const findAppliedApplianceName = (applianceIdentifier: string, shapes: DrawingShape[]) => {
        const appliance = shapes.reduce((drawingAppliance: DrawingAppliance | undefined, shape: DrawingShape) => {
            if (drawingAppliance) {
                return drawingAppliance;
            }
            return shape.appliances.find(
                (appliance) => `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` === applianceIdentifier
            );
        }, undefined);

        return appliance?.applianceModel?.consumerDesc ?? appliance?.applianceModel?.name ?? '';
    };

    const activeDrawingApplianceName: string | undefined = useMemo(() => {
        return activeDrawingAppliance ? findAppliedApplianceName(activeDrawingAppliance, drawingShapes) : undefined;
    }, [activeDrawingAppliance, drawingShapes]);

    return (
        <DrawingShapeContext.Provider
            value={{
                areThereShapesInTheDrawingArea,
                activeDrawingApplianceName,
                drawingShapes,
                updateDrawingShapes,
                addMultipleDrawingShapes,
                activeDrawingShape,
                activeDrawingItem,
                updateActiveDrawingShape,
                updateActiveDrawingAppliance,
                drawingShapesUndoStack,
                pushDrawingShapesUndoStack,
                pullDrawingShapesUndoStack,
                clearDrawingShapesUndoStack,
                resetDrawingShapes,
                autoPlaceShapeTranslation,
                resetActiveShape,
                updateCustomSideAction,
                lastActiveDrawingShape,
                updateActiveDrawingShapeId,
            }}
        >
            {children}
        </DrawingShapeContext.Provider>
    );
};
